import { useEffect, useState } from "react";
import { Steps } from "antd";
import { useTranslation } from "../../../utils/onboardingI18n";
import CompanyInfo from "./components/CompanyInfo";
import DirectorsShareholdersInfo from "./components/DirectorsShareholdersInfo";
import RepresentativeInfo from "./components/RepresentativeInfo";
import BeneficiaryInfo from "./components/BeneficiaryInfo";
import FinancialInfo from "./components/FinancialInfo";
import CorporateDeclaration from "./components/CorporateDeclaration.jsx";
import UploadDocuments from "./components/UploadDocuments";
import ApplicationStatus from "./components/ApplicationStatus";
import { getCorporateAccount } from "../../../api/request/corporate";
import Loader from "../../../components/Loader";
import { useDispatch } from "react-redux";
import { setCorporateOnboarding } from "../../../store/Slices/corporateSlice";
import { useResponsive } from "../../../hooks/useResponsive";

const { Step } = Steps;
const SUBMITTED_ACCOUNTS_KEY = "corporateApplicationSubmitted";

const readSubmittedAccountIds = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SUBMITTED_ACCOUNTS_KEY));
    return Array.isArray(stored) ? stored.map(String) : [];
  } catch (error) {
    return [];
  }
};

const rememberSubmittedAccount = (accountId) => {
  if (!accountId) return;
  const ids = readSubmittedAccountIds();
  const id = String(accountId);
  if (ids.includes(id)) return;
  localStorage.setItem(SUBMITTED_ACCOUNTS_KEY, JSON.stringify([...ids, id]));
};

const forgetSubmittedAccount = (accountId) => {
  if (!accountId) return;
  const id = String(accountId);
  const ids = readSubmittedAccountIds().filter((storedId) => storedId !== id);
  localStorage.setItem(SUBMITTED_ACCOUNTS_KEY, JSON.stringify(ids));
};

const accountIsRejected = (accountData) =>
  String(accountData?.status || "").toLowerCase() === "rejected";

const accountIsSubmitted = (accountData) => {
  if (accountIsRejected(accountData)) return false;

  const step = Number(accountData?.currentStep);
  const accountId = accountData?.id || accountData?.userId;

  if (Number(accountData?.completedPercentage) >= 100) return true;
  if (Number.isFinite(step) && step >= 8) return true;
  if (accountId && readSubmittedAccountIds().includes(String(accountId))) {
    return true;
  }

  return false;
};

const OnBoardingSteps = () => {
  const { t } = useTranslation("onBoarding");
  const dispatch = useDispatch();
  const { isMobile } = useResponsive();
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState(null);
  const [completedStep, setCompletedStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const isSubmitted = applicationSubmitted;

  const resolveStepFromAccount = (accountData) => {
    const reportedStep = Number(accountData?.currentStep);
    let step =
      Number.isFinite(reportedStep) && reportedStep > 0 ? reportedStep : 1;

    // isApplied is set as soon as the application exists, so it cannot
    // decide the screen. A rejected application stays on the reported step
    // so the user can edit and apply again.
    if (
      !accountIsRejected(accountData) &&
      Number(accountData?.completedPercentage) >= 100
    ) {
      step = 8;
    }

    if (step > 8) step = 8;

    return step;
  };

  const syncAccountProgress = async ({
    showLoader = false,
    preserveCurrentStep = false,
  } = {}) => {
    if (showLoader) setIsLoading(true);
    try {
      const { data } = await getCorporateAccount();
      if (data?.success) {
        const accountData = data?.data;
        const resolvedStep = resolveStepFromAccount(accountData);

        setData(accountData);
        const rejected = accountIsRejected(accountData);
        const submitted = accountIsSubmitted(accountData);

        if (rejected) {
          setApplicationSubmitted(false);
          forgetSubmittedAccount(accountData?.id);
          forgetSubmittedAccount(accountData?.userId);
        } else if (submitted) {
          setApplicationSubmitted(true);
          rememberSubmittedAccount(accountData?.id || accountData?.userId);
        }

        if (!preserveCurrentStep) {
          setCurrentStep(submitted ? 8 : resolvedStep);
          setCompletedStep(
            submitted ? 7 : resolvedStep >= 8 ? 7 : resolvedStep - 1
          );
        } else if (submitted) {
          setCompletedStep((prev) => Math.max(prev, 7));
        } else {
          setCompletedStep((prev) =>
            Math.max(
              prev,
              resolvedStep >= 8 ? 7 : resolvedStep - 1
            )
          );
        }

        dispatch(
          setCorporateOnboarding({
            status: accountData?.status,
            isApplied: accountData?.isApplied,
            currentStep: accountData?.currentStep,
            completedPercentage: accountData?.completedPercentage,
          })
        );
      }
    } catch (error) {
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  useEffect(() => {
    syncAccountProgress({ showLoader: true });
  }, []);

  const markApplicationSubmitted = (accountId) => {
    setApplicationSubmitted(true);
    setCompletedStep((prev) => Math.max(prev, 7));
    rememberSubmittedAccount(accountId || data?.id || data?.userId);
  };

  const onNext = () => {
    setCurrentStep((prev) => {
      setCompletedStep((completed) => Math.max(completed, prev));
      return prev + 1;
    });
    syncAccountProgress({ preserveCurrentStep: true });
  };

  const onPrevious = () => setCurrentStep((prev) => prev - 1);

  const handleStepClick = (step) => {
    if (step > completedStep + 1) return;
    setCurrentStep(step);
  };

  const steps = [
    {
      key: 1,
      title: t("Company Info"),
      content: (
        <CompanyInfo
          onNext={onNext}
          onPrevious={onPrevious}
          id={data?.companyInfoId}
          isSubmitted={isSubmitted}
        />
      ),
    },
    {
      key: 2,
      title: t("Directors/Shareholders Info"),
      content: (
        <DirectorsShareholdersInfo
          onNext={onNext}
          onPrevious={onPrevious}
          devMode={true}
          isSubmitted={isSubmitted}
        />
      ),
    },
    {
      key: 3,
      title: t("Representative Info"),
      content: (
        <RepresentativeInfo
          onNext={onNext}
          onPrevious={onPrevious}
          id={data?.representativeInfoId}
          isSubmitted={isSubmitted}
        />
      ),
    },
    {
      key: 4,
      title: t("Beneficiary Info"),
      content: (
        <BeneficiaryInfo
          onNext={onNext}
          onPrevious={onPrevious}
          isSubmitted={isSubmitted}
        />
      ),
    },
    {
      key: 5,
      title: t("Financial Info"),
      content: (
        <FinancialInfo
          onNext={onNext}
          onPrevious={onPrevious}
          id={data?.financialInfoId}
          isSubmitted={isSubmitted}
        />
      ),
    },
    {
      key: 6,
      title: t("Declaration"),
      content: (
        <CorporateDeclaration
          onNext={onNext}
          onPrevious={onPrevious}
          isSubmitted={isSubmitted}
        />
      ),
    },
    {
      key: 7,
      title: t("Upload Documents"),
      content: (
        <UploadDocuments
          onNext={onNext}
          onPrevious={onPrevious}
          onApplicationSubmitted={markApplicationSubmitted}
          id={data?.id}
          isSubmitted={isSubmitted}
        />
      ),
    },
    {
      key: 8,
      title: t("Application Status"),
      content: <ApplicationStatus status={data?.status} />,
    },
  ];

  if (isLoading) return <Loader />;

  return (
    <div className="onboarding-form w-full min-w-0">
      <div className="overflow-x-auto sticky top-0 z-20 border-b border-gray-100 bg-white py-5">
        <div className={isMobile ? "w-full" : "min-w-[720px] sm:min-w-0"}>
          <Steps
            current={currentStep - 1}
            size="small"
            direction="horizontal"
            responsive={false}
            className={isMobile ? "[&_.ant-steps-item-content]:hidden" : undefined}
          >
            {steps.map((step) => (
              <Step
                key={step?.key}
                title={isMobile ? undefined : step?.title}
                className="cursor-pointer"
                onClick={() => handleStepClick(step?.key)}
              />
            ))}
          </Steps>
        </div>
      </div>

      <div className="min-h-[400px]">{steps[currentStep - 1]?.content}</div>
    </div>
  );
};

export default OnBoardingSteps;
